import { memo, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaStepBackward,
  FaStepForward,
  FaPause,
  FaPlay,
  FaVolumeUp,
} from "react-icons/fa";
import type { RadioStation } from "./types";

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

interface TransportBtnProps {
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary";
}

const TransportBtn = memo<TransportBtnProps>(
  ({ onClick, children, variant = "secondary" }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-center rounded-full transition-colors border
      ${
        variant === "primary"
          ? "w-[36px] h-[36px] bg-white/[0.08] border-white/20 hover:bg-white/[0.14]"
          : "w-[30px] h-[30px] bg-white/[0.05] border-white/10 hover:bg-white/[0.10]"
      }`}
    >
      {children}
    </button>
  ),
);

TransportBtn.displayName = "TransportBtn";

interface Props {
  station: RadioStation | null;
  isPlaying: boolean;
  isLive: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onPrev: () => void;
  onNext: () => void;
  onVolumeChange: (v: number) => void;
  onSeek: (t: number) => void;
}

const NowPlayingBar = memo<Props>(
  ({
    station,
    isPlaying,
    isLive,
    volume,
    currentTime,
    duration,
    onPlayPause,
    onPrev,
    onNext,
    onVolumeChange,
    onSeek,
  }) => {
    const [isSeeking, setIsSeeking] = useState(false);
    const [seekTime, setSeekTime] = useState(0);

    const displayTime = isSeeking ? seekTime : currentTime;
    const progress =
      !isLive && duration > 0 ? (displayTime / duration) * 100 : 0;

    const handleTrackMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isLive) return;
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const getTime = (clientX: number) =>
          Math.round(
            Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) *
              duration,
          );

        setIsSeeking(true);
        setSeekTime(getTime(e.clientX));

        const handleMouseMove = (me: MouseEvent) =>
          setSeekTime(getTime(me.clientX));

        const handleMouseUp = (me: MouseEvent) => {
          const t = getTime(me.clientX);
          setSeekTime(t);
          setIsSeeking(false);
          onSeek(t);
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      },
      [isLive, duration, onSeek],
    );

    if (!station) return null;

    return (
      <motion.div
        className="flex items-center gap-5 px-4 py-3 rounded-sm bg-[#0c0d0d]/80 border border-white/[0.06] relative z-[1]"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 24,
          delay: 0.35,
        }}
      >
        {/* Album art — crossfades on station change */}
        <AnimatePresence mode="wait">
          <motion.img
            key={station.id + "-art"}
            src={station.thumbnail}
            alt="Now playing"
            draggable={false}
            className="w-[60px] h-[60px] rounded-lg object-cover flex-shrink-0 select-none"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{ duration: 0.2 }}
          />
        </AnimatePresence>

        {/* Song info — crossfades on station change */}
        <div className="flex-shrink-0 min-w-[160px] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={station.id + "-info"}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <p className="text-white/40 text-[11px] leading-none mb-[3px]">
                Now playing
              </p>
              <p className="text-white font-[400] text-[21px] leading-tight">
                {station.name}
              </p>
              <p className="text-white/50 text-[13px] mt-[2px]">
                {station.type}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Center block: controls stacked above track line ── */}
        <div className="flex-1 min-w-0 flex flex-col items-center gap-2.5">
          {/* Transport controls */}
          <div className="flex items-center gap-2">
            <motion.div whileTap={{ scale: 0.82 }}>
              <TransportBtn onClick={onPrev}>
                <FaStepBackward size={11} className="text-white/70" />
              </TransportBtn>
            </motion.div>
            <motion.div whileTap={{ scale: 0.82 }} whileHover={{ scale: 1.08 }}>
              <TransportBtn onClick={onPlayPause} variant="primary">
                <AnimatePresence mode="wait" initial={false}>
                  {isPlaying ? (
                    <motion.span
                      key="pause"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.6 }}
                      transition={{ duration: 0.12 }}
                    >
                      <FaPause size={12} className="text-white" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="play"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.6 }}
                      transition={{ duration: 0.12 }}
                    >
                      <FaPlay size={12} className="text-white ml-[2px]" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </TransportBtn>
            </motion.div>
            <motion.div whileTap={{ scale: 0.82 }}>
              <TransportBtn onClick={onNext}>
                <FaStepForward size={11} className="text-white/70" />
              </TransportBtn>
            </motion.div>
          </div>

          {/* Track line */}
          <div className="w-full flex items-center gap-2.5">
            <span className="text-white/70 text-[13px] flex-shrink-0 tabular-nums">
              {formatTime(displayTime)}
            </span>

            {/* Seekable track */}
            <div
              onMouseDown={handleTrackMouseDown}
              className={`relative flex-1 h-[4px] bg-white/10 rounded-full overflow-hidden
                ${!isLive ? "cursor-pointer select-none" : "cursor-default"}`}
            >
              {isLive ? (
                <div className="absolute inset-0 bg-[#137FE3]/30 animate-pulse rounded-full" />
              ) : (
                <>
                  <div
                    className="absolute inset-y-0 left-0 bg-[#137FE3] rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                  {/* Drag handle */}
                  {isSeeking && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-[10px] h-[10px] bg-white rounded-full shadow-md -ml-[5px]"
                      style={{ left: `${progress}%` }}
                    />
                  )}
                </>
              )}
            </div>

            {isLive ? (
              <span className="text-[10px] font-semibold px-1.5 py-[1px] rounded-sm bg-[#FF1F1F]/20 text-[#FF1F1F] flex-shrink-0 tracking-wide">
                LIVE
              </span>
            ) : (
              <span className="text-white/70 text-[13px] flex-shrink-0 tabular-nums">
                {formatTime(duration)}
              </span>
            )}
          </div>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <FaVolumeUp size={13} className="text-white/50" />
          <span className="text-white/50 text-[13px]">Volume</span>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            className="w-[110px] cursor-pointer h-[4px]"
            style={{ accentColor: "#137FE3" }}
          />
          <span className="text-white/70 text-[13px] w-[32px] text-right tabular-nums">
            {volume}%
          </span>
        </div>

        <div className="bg-gradient-to-l from-[#0f1f2c]/50 to-transparent absolute top-0 right-0 w-full max-w-[60%] h-full z-[-1]"></div>
        <div className="bg-gradient-to-r from-[#161331]/50 to-transparent absolute top-0 left-0 w-full max-w-[40%] h-full z-[-1]"></div>
      </motion.div>
    );
  },
);

NowPlayingBar.displayName = "NowPlayingBar";
export default NowPlayingBar;
